import React, { useCallback, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Tag } from 'primereact/tag';
import axiosInstance from '../services/axiosInstance';
import { useAllProducts } from '../hooks/useAllProducts';
import { handleApiError, handleApiSuccess } from './apiErrorHandler';

const SOURCE_LABEL = { human: 'Picked by hand', ai: 'AI', ocr: 'Standard reader' };

/** How each supplier words a product on its bills -> your catalogue product. Learned from confirmed scans; fix or remove here. */
const SupplierAliasesDialog = ({ visible, onHide }) => {
  const { data: products = [] } = useAllProducts({ enabled: visible });
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (text) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/api/supplier-aliases/', { params: text ? { q: text } : {} });
      setRows(data);
    } catch (e) { handleApiError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (visible) { setQ(''); load(''); } }, [visible, load]);
  useEffect(() => {
    if (!visible) return undefined;
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, visible, load]);

  const options = products.map((p) => ({ label: `${p.name}  (${p.code})`, value: p.id }));
  const change = async (row, product) => {
    try {
      const { data } = await axiosInstance.patch(`/api/supplier-aliases/${row.id}/`, { product });
      setRows((rs) => rs.map((r) => (r.id === row.id ? data : r)));
      handleApiSuccess('Mapping updated');
    } catch (e) { handleApiError(e); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Forget "${row.text}" for ${row.supplier_name}? The next bill will be matched from scratch.`)) return;
    try { await axiosInstance.delete(`/api/supplier-aliases/${row.id}/`); setRows((rs) => rs.filter((r) => r.id !== row.id)); } catch (e) { handleApiError(e); }
  };

  return (
    <Dialog header="Supplier product names" visible={visible} onHide={onHide} style={{ width: 'min(1100px, 96vw)' }} modal>
      <p className="mt-0 text-color-secondary">
        When you create a GRN from a scanned bill, the system remembers how that supplier words each product, so the next bill is matched automatically.
        Fix a wrong match here or remove it.
      </p>
      <IconField iconPosition="left" className="w-full mb-3">
        <InputIcon className="pi pi-search" />
        <InputText className="w-full" placeholder="Search the bill wording or the product" value={q} onChange={(e) => setQ(e.target.value)} />
      </IconField>
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
              <th>Supplier</th><th>Wording on the bill</th><th style={{ width: '32%' }}>Your product</th><th>Used</th><th>Last used</th><th>Source</th><th />
            </tr>
          </thead>
          <tbody>
            {!rows.length && <tr><td colSpan={7} className="py-3 text-color-secondary">{loading ? 'Loading...' : 'Nothing learned yet.'}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td className="py-1">{r.supplier_name}</td>
                <td>{r.text}</td>
                <td>
                  <Dropdown value={r.product} options={options.some((o) => o.value === r.product) ? options : [{ label: r.product_name, value: r.product }, ...options]}
                            onChange={(e) => change(r, e.value)} filter style={{ width: '100%' }} />
                </td>
                <td>{r.times_used}</td>
                <td>{r.last_used_at ? new Date(r.last_used_at).toLocaleDateString() : '—'}</td>
                <td><Tag severity={r.source === 'ai' ? 'info' : undefined} value={SOURCE_LABEL[r.source] || r.source} /></td>
                <td><Button icon="pi pi-trash" text severity="danger" aria-label="Remove" onClick={() => remove(r)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Dialog>
  );
};

export default SupplierAliasesDialog;
